import { lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route, Outlet } from 'react-router-dom';
import { DashboardLayout } from './components/layout/DashboardLayout';
import { AdminLayout } from './components/layout/AdminLayout';
import { isAdminHost } from './lib/host';

// Keep authentication lightweight; load feature pages when their route is visited.
const Quotes = lazy(() => import('./pages/Quotes').then(m => ({ default: m.Quotes })));
const Orders = lazy(() => import('./pages/Orders').then(m => ({ default: m.Orders })));
const OrderDetails = lazy(() => import('./pages/OrderDetails').then(m => ({ default: m.OrderDetails })));
const Parts = lazy(() => import('./pages/Parts').then(m => ({ default: m.Parts })));
const Account = lazy(() => import('./pages/Account').then(m => ({ default: m.Account })));
const Drafts = lazy(() => import('./pages/Drafts').then(m => ({ default: m.Drafts })));
const CustomerHome = lazy(() => import('./pages/CustomerHome').then(m => ({ default: m.CustomerHome })));
const QuoteRFQs = lazy(() => import('./pages/QuoteRFQs').then(m => ({ default: m.QuoteRFQs })));
const SharedQuote = lazy(() => import('./pages/SharedQuote').then(m => ({ default: m.SharedQuote })));
const NotificationPanel = lazy(() => import('./components/notifications/NotificationPanel').then(m => ({ default: m.NotificationPanel })));
import { Login } from './pages/Login';
import { AdminLogin } from './pages/AdminLogin';
import { ResetPassword } from './pages/ResetPassword';
import { PaymentSuccess } from './pages/PaymentSuccess';
import { PaymentCancel } from './pages/PaymentCancel';

// Heavy pages — lazy loaded (three.js, WASM, PDF, Stripe, large admin UI)
const NewQuote = lazy(() => import('./pages/NewQuote').then(m => ({ default: m.NewQuote })));
const Admin = lazy(() => import('./pages/Admin').then(m => ({ default: m.Admin })));
const AdminDashboard = lazy(() => import('./pages/AdminDashboard').then(m => ({ default: m.AdminDashboard })));
const Checkout = lazy(() => import('./pages/Checkout').then(m => ({ default: m.Checkout })));
const ManualReviewCheckout = lazy(() => import('./pages/ManualReviewCheckout').then(m => ({ default: m.ManualReviewCheckout })));
const ProductionOverview = lazy(() => import('./pages/ProductionOverview').then(m => ({ default: m.ProductionOverview })));
const MaintenancePage = lazy(() => import('./pages/MaintenancePage').then(m => ({ default: m.MaintenancePage })));
const Employee = lazy(() => import('./pages/Employee').then(m => ({ default: m.Employee })));
const EmployeeOrderDetails = lazy(() => import('./pages/EmployeeOrderDetails').then(m => ({ default: m.EmployeeOrderDetails })));
const BulkCostEntry = lazy(() => import('./pages/BulkCostEntry').then(m => ({ default: m.BulkCostEntry })));
const Customers = lazy(() => import('./pages/Customers').then(m => ({ default: m.Customers })));
const Schedule = lazy(() => import('./pages/Schedule'));
const AdminWorkspace = lazy(() => import('./pages/AdminWorkspace').then(m => ({ default: m.AdminWorkspace })));
const AnalyticsDashboard = lazy(() => import('./components/admin/AnalyticsDashboard').then(m => ({ default: m.AnalyticsDashboard })));
const StaffSettings = lazy(() => import('./pages/StaffSettings').then(m => ({ default: m.StaffSettings })));
const OrdersPage = lazy(() => import('./pages/OrdersPage').then(m => ({ default: m.OrdersPage })));

import { AuthProvider } from './components/auth/AuthProvider';
import { ProtectedRoute } from './components/auth/ProtectedRoute';

import * as Sentry from "@sentry/react";

// Lightweight fallback shown while heavy chunks are being fetched
function PageLoader() {
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', background: '#0f0f0f' }}>
      <div style={{ width: 36, height: 36, border: '3px solid #333', borderTopColor: '#6366f1', borderRadius: '50%', animation: 'spin 0.7s linear infinite' }} />
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}

function App() {
  const adminHost = isAdminHost();

  return (
    <Sentry.ErrorBoundary fallback={<p>An error has occurred</p>}>
      <AuthProvider>
        <BrowserRouter>
          <Suspense fallback={<PageLoader />}>
            {adminHost ? (
              /* ─── Admin Subdomain (admin.printmyparts.io) ─── */
              <Routes>
                <Route path="/login" element={<AdminLogin />} />
                <Route path="/reset-password" element={<ResetPassword />} />

                {/* Admin + Employee routes — wrapped in AdminLayout */}
                <Route element={<ProtectedRoute allowedRoles={['admin', 'employee']} />}>
                  <Route element={<AdminLayout><Outlet /></AdminLayout>}>
                    <Route path="/settings" element={<StaffSettings />} />
                    <Route element={<ProtectedRoute requiredPermission="dashboard" />}>
                      <Route path="/" element={<AdminDashboard />} />
                    </Route>
                    <Route element={<ProtectedRoute requiredPermission="production" />}>
                      <Route path="/production" element={<Employee />} />
                      <Route path="/production/orders/:id" element={<EmployeeOrderDetails />} />
                      <Route path="/admin/messages" element={<AdminWorkspace mode="messages" />} />
                      <Route path="/admin/orders" element={<OrdersPage view="list" />} />
                      <Route path="/admin/orders/list" element={<OrdersPage view="list" />} />
                      <Route path="/admin/orders/board" element={<OrdersPage view="board" />} />
                      <Route path="/admin/orders/calendar" element={<OrdersPage view="calendar" />} />
                      <Route path="/production/parts" element={<AdminWorkspace mode="parts" />} />
                      <Route path="/production/inspection" element={<AdminWorkspace mode="inspection" />} />
                      <Route path="/production/shipping" element={<AdminWorkspace mode="shipping" />} />
                    </Route>
                    <Route element={<ProtectedRoute requiredPermission="overview" />}>
                      <Route path="/production/overview" element={<ProductionOverview />} />
                    </Route>
                    <Route element={<ProtectedRoute requiredPermission="schedule" />}>
                      <Route path="/production/schedule" element={<Schedule />} />
                    </Route>
                    <Route element={<ProtectedRoute requiredPermission="maintenance" />}>
                      <Route path="/production/maintenance" element={<MaintenancePage />} />
                    </Route>
                    <Route element={<ProtectedRoute requiredPermission="drafts" />}>
                      <Route path="/admin/drafts" element={<Drafts />} />
                    </Route>
                    <Route element={<ProtectedRoute requiredPermission="customers" />}>
                      <Route path="/admin/customers" element={<Customers />} />
                    </Route>
                    <Route element={<ProtectedRoute requiredPermission="rfqs" />}>
                      <Route path="/admin/requests" element={<QuoteRFQs />} />
                      <Route path="/admin/quotes" element={<AdminWorkspace mode="quotes" />} />
                    </Route>
                  </Route>
                </Route>

                {/* Admin-only routes */}
                <Route element={<ProtectedRoute allowedRoles={['admin']} />}>
                  <Route element={<AdminLayout><Outlet /></AdminLayout>}>
                    <Route path="/admin" element={<Admin />} />
                    <Route path="/admin/bulk-import" element={<BulkCostEntry />} />
                    <Route path="/admin/analytics" element={<AnalyticsDashboard />} />
                    <Route path="/admin/projects" element={<AdminWorkspace mode="projects" />} />
                  </Route>
                </Route>
              </Routes>
            ) : (
              /* ─── Customer App (printmyparts.io) ─── */
              <Routes>
                <Route path="/login" element={<Login />} />
                <Route path="/reset-password" element={<ResetPassword />} />
                <Route path="/share/:token" element={<SharedQuote />} />
                <Route path="/payment-success" element={<PaymentSuccess />} />
                <Route path="/payment-cancel" element={<PaymentCancel />} />

                <Route path="/new" element={<DashboardLayout><NewQuote /></DashboardLayout>} />
                <Route path="/checkout/:quoteId" element={<DashboardLayout><Checkout /></DashboardLayout>} />
                <Route path="/checkout/manual/:quoteId" element={<DashboardLayout><ManualReviewCheckout /></DashboardLayout>} />

                <Route element={<ProtectedRoute />}>
                  <Route element={<DashboardLayout><Outlet /></DashboardLayout>}>
                    <Route path="/" element={<CustomerHome />} />
                    <Route path="/home" element={<CustomerHome />} />
                    <Route path="/notifications" element={<NotificationPanel />} />
                    <Route path="/quotes" element={<Quotes />} />
                    <Route path="/orders" element={<Orders />} />
                    <Route path="/orders/:id" element={<OrderDetails />} />
                    <Route path="/parts" element={<Parts />} />
                    <Route path="/account" element={<Account />} />
                  </Route>
                </Route>

                {/* Admin Protected Routes */}
                <Route element={<ProtectedRoute allowedRoles={['admin']} />}>
                  <Route element={<DashboardLayout><Outlet /></DashboardLayout>}>
                    <Route path="/admin" element={<Admin />} />
                    <Route path="/admin/requests" element={<QuoteRFQs />} />
                    <Route path="/admin/drafts" element={<Drafts />} />
                    <Route path="/admin/customers" element={<Customers />} />
                    <Route path="/admin/bulk-import" element={<BulkCostEntry />} />
                  </Route>
                </Route>

                {/* Production Protected Routes (Admin or Employee) */}
                <Route element={<ProtectedRoute allowedRoles={['admin', 'employee']} />}>
                  <Route element={<DashboardLayout><Outlet /></DashboardLayout>}>
                    <Route path="/production" element={<Employee />} />
                    <Route path="/production/overview" element={<ProductionOverview />} />
                    <Route path="/production/schedule" element={<Schedule />} />
                    <Route path="/production/orders/:id" element={<EmployeeOrderDetails />} />
                  </Route>
                </Route>
              </Routes>
            )}
          </Suspense>
        </BrowserRouter>
      </AuthProvider>
    </Sentry.ErrorBoundary>
  );
}

export default App;
